import React, { useMemo, useRef } from 'react';
import { arrayOf, string } from 'prop-types';
import classNames from 'classnames';
import { Carousel, ListingCardCompact, NamedLink } from '../../components';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { useSelector } from 'react-redux';
import { createURLParamsForListing } from '../../util/listingHelpers';
import ListingService from '../../services/ListingService';
import { LOCALE_TO_LANG } from '../../constants/locales';

import css from './SeoPage.module.css';
import { useConfiguration } from '../../context/configurationContext';

const ListingSection = props => {
  const { rootClassName, className, listings, sectionTitle } = props;
  const { currentUser } = useSelector(state => state.user);
  const { interfaceLang } = useSelector(state => ({ interfaceLang: state.ui.interfaceLang }));
  const intl = useIntl();
  const config = useConfiguration();

  const classes = useMemo(() => classNames(rootClassName || css.root, className), [
    className,
    rootClassName,
  ]);

  const hasListings = listings && listings.length > 0;
  const sliderRef = useRef();

  const handlePrev = () => {
    if (sliderRef.current) sliderRef.current.slickPrev();
  };
  const handleNext = () => {
    if (sliderRef.current) sliderRef.current.slickNext();
  };

  const isSSR = typeof window === 'undefined';

  const carousel = useMemo(
    () =>
      hasListings && !isSSR && (
        <Carousel
          ref={sliderRef}
          columns={5}
          className={css.carousel}
          contentClassName={css.carouselContent}
          showArrows={false}
        >
          {listings.map(l => (
            <ListingCardCompact
              key={l.id.uuid}
              className={css.listingCard}
              listing={l}
              currentUser={currentUser}
              config={config}
            />
          ))}
        </Carousel>
      ),
    [config, currentUser, listings, hasListings, isSSR]
  );

  return (
    <div className={classes}>
      {/* Hidden navigation links for SEO crawlers - using sr-only pattern */}
      {hasListings && (
        <nav className="sr-only" style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}>
          <ul>
            {listings.map(listing => {
              // Determine localized display title: prefer explicit per-language titles from publicData,
              // fall back to ListingService.createTitle which composes a translated title.
              const locale = interfaceLang || intl.locale;
              const language = LOCALE_TO_LANG[locale] || 'english';
              const pd = listing?.attributes?.publicData || {};
              const titleFromPublicData = pd.titles && pd.titles[language];
              const generatedTitle = ListingService.createTitle(intl, listing, language);
              const title = titleFromPublicData || generatedTitle || listing?.attributes?.title || listing?.title || listing?.attributes?.name || listing?.id?.uuid;
              const urlParams = createURLParamsForListing(listing, interfaceLang, intl);
              return (
                <li key={listing.id.uuid}>
                  <NamedLink name="ListingPage" params={urlParams}>
                    {title}
                  </NamedLink>
                </li>
              );
            })}
          </ul>
        </nav>
      )}

      <div className={css.listingsContent}>
        {hasListings && (
          <div>
            <div className={css.headerRow}>
              <h2 className={css.title}>
                {sectionTitle ? (
                  sectionTitle
                ) : (
                  <FormattedMessage id="SectionListings.popularListingsTitle" />
                )}
              </h2>
              <div className={css.carouselArrows}>
                <button className={css.arrowBtn} onClick={handlePrev} aria-label="Previous">
                  ‹
                </button>
                <button className={css.arrowBtn} onClick={handleNext} aria-label="Next">
                  ›
                </button>
              </div>
            </div>
            <div className={css.carouselWrapper}>{carousel}</div>
          </div>
        )}
      </div>
    </div>
  );
};

ListingSection.defaultProps = {
  rootClassName: null,
  className: null,
  listings: [],
  sectionTitle: null,
};

ListingSection.propTypes = {
  rootClassName: string,
  className: string,
  listings: arrayOf(propTypes.listing),
  sectionTitle: string,
};

export default ListingSection;
